/**
 * Respawn ticket system: ticket tracking, cooldown, respawn execution.
 * Pure functions — no side effects, no rendering.
 */

import type { GameState, PlayerState, RespawnTickets, CrashedDropCanState } from '../types';
import { BASE_MOVEMENT_RANGE } from '../constants';
import { dcos, dsin } from './dmath';

// =============================================================================
// INITIALIZATION
// =============================================================================

/**
 * Create initial respawn state for all teams.
 */
export function initRespawnState(
  players: PlayerState[],
  ticketsPerTeam: number
): RespawnTickets {
  const teamIndices = new Set(players.map(p => p.teamIndex));
  const tickets: Record<number, number> = {};
  const cooldowns: Record<number, number> = {};

  for (const ti of teamIndices) {
    tickets[ti] = ticketsPerTeam;
  }

  for (const p of players) {
    cooldowns[p.id] = 0;
  }

  return { tickets, cooldowns };
}

// =============================================================================
// COOLDOWN MANAGEMENT
// =============================================================================

/**
 * Decrement all cooldowns by 1 (called on full turn wrap). Floors at 0.
 */
export function tickCooldowns(respawn: RespawnTickets): RespawnTickets {
  const newCooldowns: Record<number, number> = {};
  for (const [idStr, cd] of Object.entries(respawn.cooldowns)) {
    newCooldowns[Number(idStr)] = Math.max(0, cd - 1);
  }
  return { ...respawn, cooldowns: newCooldowns };
}

/**
 * Set cooldown on a player after death.
 */
export function addDeathCooldown(
  respawn: RespawnTickets,
  playerId: number,
  cooldownTurns: number
): RespawnTickets {
  return {
    ...respawn,
    cooldowns: { ...respawn.cooldowns, [playerId]: cooldownTurns },
  };
}

// =============================================================================
// RESPAWN CHECK & EXECUTION
// =============================================================================

/**
 * Check if a player can respawn (cooldown expired + team has tickets).
 */
export function canRespawn(
  playerId: number,
  teamIndex: number,
  respawn: RespawnTickets
): boolean {
  const cooldown = respawn.cooldowns[playerId] ?? 0;
  if (cooldown > 0) return false;

  const tickets = respawn.tickets[teamIndex] ?? 0;
  return tickets > 0;
}

/**
 * Execute a respawn: revive player at home planet, full health, deduct ticket.
 */
export function executeRespawn(
  state: GameState,
  playerId: number
): GameState {
  const player = state.players.find(p => p.id === playerId);
  if (!player) return state;
  if (player.alive) return state; // already alive
  if (player.extracted) return state; // X22: got out — not coming back this battle

  const modeState = state.modeState;
  const respawn = modeState.respawn;
  if (!respawn || !canRespawn(playerId, player.teamIndex, respawn)) return state;

  // Find home planet — CTF uses homeTeamPlanets, others fall back to player's last planet
  const homeTeamPlanets = state.config.modeConfig.homeTeamPlanets;
  let homePlanet = homeTeamPlanets?.[player.teamIndex];
  if (homePlanet === undefined) {
    // Fallback: respawn at the planet the player was on
    homePlanet = player.planetIndex;
  }

  // Spawn near team's crashed drop can if available
  const dropCan = state.crashedDropCans.find(
    (c: CrashedDropCanState) => c.teamIndex === player.teamIndex && c.planetIndex === homePlanet && c.health > 0
  );
  let surfaceDir: { x: number; y: number; z: number };
  if (dropCan) {
    surfaceDir = { ...dropCan.surfaceDir };
  } else {
    // Fallback: deterministic but varied position
    const angle = (playerId * 2.399 + state.turnNumber * 0.7) % (Math.PI * 2);
    const phi = Math.PI / 3 + ((playerId * 1.618 + state.turnNumber * 0.3) % 1) * (Math.PI / 3);
    surfaceDir = {
      x: dsin(phi) * dcos(angle),
      y: dcos(phi),
      z: dsin(phi) * dsin(angle),
    };
  }

  // Revive player
  const newPlayers = state.players.map(p =>
    p.id === playerId
      ? {
          ...p,
          alive: true,
          health: 100,
          planetIndex: homePlanet,
          surfaceDir,
          airborne: false,
          airbornePos: null,
          airborneVel: null,
          movementRemaining: p.movementRange,
          lastAimAzimuth: 0,
          lastAimElevation: Math.PI / 4,
        }
      : p
  );

  // Deduct ticket
  const newTickets = { ...respawn.tickets };
  if (!respawn.unlimited?.includes(player.teamIndex)) newTickets[player.teamIndex] = Math.max(0, (newTickets[player.teamIndex] ?? 0) - 1);   // 69.6d: an unlimited team spends none

  const newRespawn: RespawnTickets = {
    ...respawn,
    tickets: newTickets,
  };

  return {
    ...state,
    players: newPlayers,
    modeState: { ...modeState, respawn: newRespawn },
  };
}

// =============================================================================
// TICKET MANAGEMENT
// =============================================================================

/**
 * Deduct tickets from a team (e.g., on capture penalty). Floors at 0.
 */
export function drainTickets(
  respawn: RespawnTickets,
  teamIndex: number,
  amount: number
): RespawnTickets {
  const current = respawn.tickets[teamIndex] ?? 0;
  return {
    ...respawn,
    tickets: { ...respawn.tickets, [teamIndex]: Math.max(0, current - amount) },
  };
}

/**
 * Get remaining tickets for a team.
 */
export function getTeamTickets(
  respawn: RespawnTickets,
  teamIndex: number
): number {
  return respawn.tickets[teamIndex] ?? 0;
}

/**
 * Check if a team is permanently dead (no tickets + all members dead).
 */
export function isTeamPermadead(
  teamIndex: number,
  respawn: RespawnTickets,
  players: PlayerState[]
): boolean {
  const tickets = respawn.tickets[teamIndex] ?? 0;
  if (tickets > 0 || respawn.unlimited?.includes(teamIndex)) return false;

  const teamPlayers = players.filter(p => p.teamIndex === teamIndex);
  return teamPlayers.every(p => !p.alive);
}
